import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const EmailHistory = ({ contactId, companyId, title = "Email History" }) => {
  const [gmailStatus, setGmailStatus] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkGmailStatusAndLoadEmails();
  }, [contactId, companyId]);

  const checkGmailStatusAndLoadEmails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check Gmail connection status
      const statusResponse = await fetch('/api/gmail/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to check Gmail status');
      }

      const status = await statusResponse.json();
      setGmailStatus(status);

      if (status.connected) {
        // Gmail is connected, load real emails
        try {
          const emailsResponse = await fetch(`/api/gmail/emails?contact_id=${contactId || ''}&company_id=${companyId || ''}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (emailsResponse.ok) {
            const emailsData = await emailsResponse.json();
            setEmails(emailsData || []);
          } else {
            console.warn('Failed to load emails:', emailsResponse.statusText);
            setEmails([]);
          }
        } catch (emailError) {
          console.warn('Error loading emails:', emailError);
          setEmails([]);
        }
      } else {
        setEmails([]);
      }

    } catch (err) {
      console.error('Error loading email data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Mail className="w-5 h-5" />
            <span>{title}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2 animate-spin" />
            <p className="text-gray-600">Loading email history...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Mail className="w-5 h-5" />
            <span>{title}</span>
          </div>
          {gmailStatus && (
            <Badge 
              className={gmailStatus.connected 
                ? "bg-green-100 text-green-800" 
                : "bg-gray-100 text-gray-800"
              }
            >
              {gmailStatus.connected ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Connected
                </>
              ) : (
                'Not Connected'
              )}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {!gmailStatus?.connected ? (
          <div className="text-center py-8">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Gmail Not Connected</h3>
            <p className="text-gray-600 text-sm mb-3">
              Connect your Gmail account to view email history for this contact.
            </p>
            <p className="text-blue-600 text-xs">
              Go to <strong>Settings → Integrations</strong> to connect Gmail.
            </p>
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Emails Found</h3>
            <p className="text-gray-600 text-sm">
              No email conversations found for this contact yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-3">
              {emails.length} email{emails.length !== 1 ? 's' : ''} found
              {gmailStatus.gmail_address && (
                <span className="ml-2 text-xs text-gray-500">
                  from {gmailStatus.gmail_address}
                </span>
              )}
            </p>
            
            {emails.map((email) => (
              <div 
                key={email.id}
                className={`border rounded-lg p-3 hover:bg-gray-50 cursor-pointer ${
                  !email.isRead ? 'bg-blue-50 border-blue-200' : 'bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className={`text-sm ${!email.isRead ? 'font-semibold' : 'font-medium'}`}>
                        {email.subject}
                      </p>
                      {!email.isRead && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mb-1">
                      From: {email.sender}
                    </p>
                    <p className="text-sm text-gray-700">
                      {email.snippet}
                    </p>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="text-xs text-gray-500">
                      {formatDate(email.date)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailHistory;